import { ReferenceType } from '../../../../../../../../../../../backend/src/types/types';
import { FactDetails } from '@/api/types/backend-dto-types';
import { FactAccordionHeader } from '@/new/components/fact/fact-accordion';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Skeleton } from '@/new/components/shadcn/skeleton';

export function EndBlockFactsList({
  subsetId,
  facts,
  isLoading,
  factIds,
  selectedFactIds,
  setSelectedFactIds,
  referenceIndexMap,
  onReferenceClick,
  actionsMenu,
}: {
  subsetId: string;
  facts: FactDetails[];
  isLoading: boolean;
  factIds: string[];
  selectedFactIds: Set<string>;
  setSelectedFactIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
  actionsMenu: React.ReactNode;
}) {
  const allRowsChecked = facts.length > 0 && selectedFactIds.size === facts.length;

  const toggleAllHandler = () => {
    if (allRowsChecked) {
      setSelectedFactIds(new Set());
    } else {
      setSelectedFactIds(new Set(facts.map((f) => f.id)));
    }
  };

  return (
    <>
      {!isLoading && (
        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 gap-y-4 pr-3.5 pl-5.25">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Checkbox checked={allRowsChecked} onCheckedChange={toggleAllHandler} className="mr-2" />
            <p>
              <strong>{selectedFactIds.size.toLocaleString()}</strong> Facts selected
            </p>

            <Button
              size="sm"
              variant={'ghost'}
              disabled={selectedFactIds.size === 0}
              onClick={() => setSelectedFactIds(new Set())}
              className="gap-1"
            >
              <Icon className="lucide--x" />
              Clear all
            </Button>
          </div>
          {actionsMenu}
        </div>
      )}

      <div className="mt-2.5 flex flex-col gap-2 px-1">
        {isLoading &&
          Array.from({ length: Math.min(factIds.length, 3) }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-xl bg-gray-100" />
          ))}

        {!isLoading &&
          facts.map((fact) => (
            <div key={fact.id} className="flex items-start gap-3">
              <button
                data-state="closed"
                className="group min-w-0 flex-1 cursor-pointer rounded-xl border bg-white px-4 py-3 pt-2.75 pr-3 text-left text-[13px] ring-offset-2 transition-colors duration-150 hover:border-gray-300 focus-visible:ring-1 focus-visible:ring-teal-500 focus-visible:outline-none"
                onClick={() => onReferenceClick?.(fact.id, ReferenceType.Fact)}
              >
                <FactAccordionHeader
                  selectable={{
                    isSelected: selectedFactIds.has(fact.id),
                    handleSelect: (checked) => {
                      setSelectedFactIds((prev) => {
                        const next = new Set(prev);
                        if (checked) {
                          next.add(fact.id);
                        } else {
                          next.delete(fact.id);
                        }
                        return next;
                      });
                    },
                  }}
                  reference={referenceIndexMap.get(fact.id)?.index}
                  editable={false}
                  onViewDocument={() => {
                    onReferenceClick?.(fact.id, ReferenceType.Fact);
                  }}
                  fact={fact}
                  subsetId={subsetId}
                />
              </button>
            </div>
          ))}
      </div>
    </>
  );
}

export function EndBlockFactsActionsMenu({
  onCreateChronology,
  onAddToExistingChronology,
  onSelectOnPage,
  disabled,
  isCreatingChronology = false,
  isChronology = false,
}: {
  onCreateChronology: () => void;
  onAddToExistingChronology: () => void;
  onSelectOnPage: () => void;
  disabled: boolean;
  isCreatingChronology?: boolean;
  isChronology?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      {!isChronology && (
        <DropdownMenu size="sm">
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="white" disabled={disabled || isCreatingChronology}>
              {isCreatingChronology ? (
                <Icon className="lucide--loader animate-spin ease-in-out" />
              ) : (
                <Icon className="lucide--git-fork" />
              )}
              Fact Chronology
              <Icon className="lucide--chevron-down" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" align="end">
            <DropdownMenuItem onClick={onCreateChronology}>
              <Icon className="lucide--plus" />
              Create new from selected facts
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onAddToExistingChronology}>
              <Icon className="lucide--git-branch-plus" />
              Add selected facts to existing
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <DropdownMenu size="sm">
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" className="my-0.75" disabled={disabled}>
            <Icon className="lucide--more-horizontal" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <DropdownMenuItem onClick={onSelectOnPage}>
            <Icon className="lucide--list-checks" />
            Select facts on facts page
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
